import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { Footer, Heading } from 'components/atomic/atoms';
import { HeaderOrganism } from 'components/atomic/organism';

export default function About() {
  return (
    <div className='text-nero'>
      <HeaderOrganism heading='About Us' />
      <div className='bg-lightpink h-240 md:h-200 2xl:h-148'>
        <main className='flex flex-wrap content-start flex-col md:text-lg md:w-4/5 mx-auto mb-10 text-justify leading-8 font-normal'>
          <div className='p-8 md:p-14 bg-white mt-14 rounded-xl w-11/12 md:w-full mx-auto'>
            <Heading
              level={2}
              className='text-center font-bold text-2xl md:text-3xl'
            >
              About Hafla
            </Heading>
            <div className='md:mt-5 mt-2 leading-relaxed md:leading-8'>
              <p className='md:text-center'>
                {`Hafla is the world's first Artificial Intelligence-based event
                ideation, recommendation and aggregated sourcing platform for
                venues, equipment, services and catering. By delivering
                personalised solutions through its interactive one-stop-shop for
                events - corporate and personal, large and small - Hafla
                minimizes the stress in event planning so that everyone can
                celebrate more.`}
              </p>
            </div>
          </div>

          <div className='flex flex-row flex-wrap'>
            <div className='md:flex-1 md:me-4 bg-snowwhite p-8 text-justify rounded-xl mt-10 w-11/12 mx-auto'>
              <p className='leading-relaxed md:leading-8'>
                Every event is a celebration - occasions, festivals, personal or
                professional milestones, ideas, innovation, great weather or
                even an important life moment. Hafla aims to inspire and enable
                everyone to celebrate.
              </p>
              <p className='mt-4 leading-relaxed md:leading-8'>
                Our name, Hafla, is the Arabic word for a party, ceremony,
                celebration or happy event. We see Ha.F.La as an acronym for
                Happiness. Fun. Laughter – the essence of human life.
              </p>
              <p className='mt-4 leading-relaxed md:leading-8'>
                Founded by people with years of experience in the event
                industry, and backed by data from more than 25,000 events, Hafla
                provides a much-needed tech-enabled one-stop-shop for all event
                services. We apply data and on-the-ground experience from
                thousands of events to build algorithms and smart solutions that
                will make event planning personalised, joyful, and simple.
                Through a combination of technology and a passion for events,
                team Hafla aims to create a tailor-made experience for your
                guests and you.
              </p>
            </div>

            <div className='md:flex-1 bg-snowwhite p-8 text-justify rounded-xl mt-10 w-11/12 mx-auto'>
              <p className='leading-relaxed md:leading-8'>
                Hafla provides end-to-end event solutions powered by proprietary
                data and algorithms and executed by partners vetted for quality.
                With the largest selection of equipment, venues, services and
                catering options, and led by a group of passionate event
                experts, Hafla is transforming how events are conceptualized and
                booked.
              </p>
              <p className='mt-4 leading-relaxed md:leading-8'>
                As the only digital one-stop shop for event services, Hafla
                brings together the largest inventory for venues, furniture
                rentals, tents and marquees setups, custom-built installations,
                audio-visual equipment, bouncy castles, inflatable games, crowd
                control equipment, kids birthday packages, balloon decoration,
                and entertainment options. We can hook the event up with live
                musicians, magicians, face painters, game hosts, talent shows,
                catering and anything else you may think of.
              </p>
            </div>
          </div>

          <div className='bg-snowwhite p-8 mx-auto mt-10 text-justify rounded-xl leading-relaxed md:leading-8 md:w-full w-11/12'>
            <p>
              The platform understands the event context, creates personalized
              event ideas (based on user data, context, availability, and
              pricing) and then filters and aggregates supply from thousands of
              providers to set up the perfect event. What may have taken days,
              can now be done in minutes! Think of any event - corporate events,
              conferences, workshops and exhibitions, weddings and engagements,
              birthday parties, baby showers, Christmas dinners, new year
              parties, Diwali celebrations, Eid celebrations, National Day,
              sporting events, carnivals, food festivals, family days or gala
              dinners - Hafla can help with ideas, collaboration and bookings
              for everything for your event. On the day of the event, Hafla will
              manage all logistics and payments so you can focus on enjoying the
              experience.
            </p>
          </div>

          <div className='flex flex-col bg-lightpink mt-10 rounded-xl mb-16'>
            <Heading
              level={3}
              className='text-center font-bold text-2xl md:text-3xl p-10 text-nero'
            >
              The vision, purpose, mission and culture
            </Heading>
            <div className='bg-white text-center p-8 w-11/12 mx-auto rounded-2xl'>
              <h4 className='text-2xl'>The vision</h4>
              <p className='md:mt-4 mt-2 md:text-center text-justify leading-relaxed md:leading-8'>
                Inspire and enable everyone to celebrate more.
              </p>
            </div>

            <div className='flex flex-row w-11/12 mx-auto flex-wrap'>
              <div className='md:flex-1 bg-white p-8 rounded-xl md:me-4 text-justify mt-10'>
                <Heading
                  level={4}
                  className='text-center text-2xl'
                >
                  The purpose
                </Heading>
                <p className='md:mt-5 mt-2 leading-relaxed md:leading-8'>
                  We exist so that everyone can celebrate more and easily.
                </p>
                <p className='mt-4 leading-relaxed md:leading-8'>
                  Life has so much to celebrate - festivals, occasions, personal
                  and professional milestones, ideas and knowledge, technology,
                  progress, new products, great weather and sunshine, movies,
                  sports, friendship and above all – life itself. Yet, the world
                  celebrates less because of the time, effort and money
                  required. The stress of organizing and planning an event far
                  supersedes the joy - so people give up celebrating.
                </p>
                <div>
                  <ul className='mt-4 list-disc list-outside font-bold ml-5 about leading-relaxed md:leading-8'>
                    <li>We exist to reduce the stress of organizing events.</li>
                    <li>We exist to make event planning easy.</li>
                    <li>We exist to celebrate life.</li>
                  </ul>
                </div>
              </div>

              <div className='md:flex-1 bg-white p-8 rounded-xl text-justify mt-10'>
                <Heading
                  level={4}
                  className='text-center text-2xl'
                >
                  The mission
                </Heading>
                <p className='md:mt-5 mt-2 leading-relaxed md:leading-8'>
                  We are on a mission to{' '}
                  <b>deliver more and better celebrations</b>. We want to let
                  you <b>tailor your event</b>. We want to make{' '}
                  <b>event planning easy</b> by saving time and effort.
                </p>
                <div>
                  <ul className='list-disc list-outside ml-5 about leading-relaxed md:leading-8'>
                    <li className='mt-4'>
                      <b>Deliver more and better celebrations</b>
                      <br />
                      Unique inspirational ideas; collaboration with friends,
                      family, and colleagues; a range of products and services
                      that suit your budget; quality on-time service
                    </li>
                    <li className='mt-4'>
                      <b>Be the master of your event</b>
                      <br />
                      Driven by data and AI, the application aims to tailor your
                      experiences to suit your needs
                    </li>
                    <li className='mt-4'>
                      <b>Make event planning easy</b>
                      <br />
                      One-stop-shop; user-friendly interactive platform; event
                      day tracking and streamlined payments
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className='bg-white text-center p-8 w-11/12 mx-auto rounded-2xl my-10 leading-relaxed md:leading-8'>
              <Heading
                level={4}
                className='text-center text-2xl'
              >
                The culture
              </Heading>
              <p className='mt-4 md:text-center text-justify'>
                A love for celebrating life unites us. Team Hafla collectively -
                shares the following attributes:
              </p>
              <div className='mt-4 font-bold leading-8 md:text-center text-justify'>
                <p>We are passionate and diligent</p>
                <p>We are perseverant</p>
                <p>We find problems to solve, not to complain</p>
                <p>We are honest in success and in failure</p>
                <p>We are optimistic, friendly and happy</p>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}

export async function getStaticProps({ locale = 'en' }) {
  const values = await Promise.all([
    serverSideTranslations(locale, ['footer'])
  ]);

  return {
    props: {
      ...values[0]
    }
  };
}
